import React, { useCallback, useEffect, useState } from 'react';
import { Viewer, Worker, Icon, Position, Tooltip } from '@react-pdf-viewer/core';
import { toolbarPlugin } from '@react-pdf-viewer/toolbar';
import { Typography, CircularProgress, Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import SearchIcon from "@material-ui/icons/Search";
import { ConnectService } from "@services";
import { isMobile } from "react-device-detect";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import clsx from "clsx";
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/toolbar/lib/styles/index.css';

const useStyles = makeStyles(theme => ({
    centerLoading: {
        [theme.breakpoints.up('sm')]: {
            left: '62%',
            top: '50%',
        },
        position: 'absolute',
        left: '50%',
        top: '75%',
        '-webkit-transform': 'translate(-50%, -50%)',
        transform: 'translate(-50%, -50%)',
    },
    searchButtons: {
        background: '#fff',
        border: 'none',
        height: '100%',
        padding: '0 2px',
        cursor: 'pointer',
    },
    searchIcon: {
        display: 'flex', 
        alignItems: 'center',
        paddingRight: theme.spacing(1),
        borderRight: '1px solid grey'
    }
}));

const PDFViewerAndToolBar = ({ id, title, image, isDownloadable, pdfType }) => {
    const classes = useStyles();

    const [pdfData, setPdfData] = useState(null);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [loading, setLoading] = useState(null);

    const toolbarPluginInstance = toolbarPlugin();
    const { Toolbar } = toolbarPluginInstance;

    const renderPDFSearch = (Search) => {
        return (
            <Search>
                {(renderSearchProps) => {
                    return (
                        <>
                            <div style={{ display: 'flex' }}>
                                <div
                                    style={{
                                        border: '1px solid rgba(0, 0, 0, 0.3)',
                                        display: 'flex',
                                    }}
                                >
                                    <input
                                        style={{
                                            border: 'none',
                                            padding: '8px',
                                            width: '200px',
                                            fontSize: '16px',
                                        }}
                                        placeholder="Enter to search"
                                        type="text"
                                        value={renderSearchProps.keyword}
                                        onChange={(e) => {
                                            renderSearchProps.setKeyword(e.target.value);
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.keyCode === 13 && renderSearchProps.keyword) {
                                                renderSearchProps.search();
                                            }
                                        }}
                                    />
                                    <div onClick={() => renderSearchProps.search()} className={clsx(classes.searchButtons, classes.searchIcon)}>
                                        <SearchIcon />
                                    </div>
                                    <Tooltip
                                        position={Position.BottomCenter}
                                        target={
                                            <button
                                                className={classes.searchButtons}
                                                style={{
                                                    borderBottom: `2px solid ${
                                                        renderSearchProps.matchCase ? 'blue' : 'transparent'
                                                    }`,
                                                }}
                                                onClick={() =>
                                                    renderSearchProps.changeMatchCase(!renderSearchProps.matchCase)
                                                }
                                            >
                                                <Icon>
                                                    <path d="M15.979,21.725,9.453,2.612a.5.5,0,0,0-.946,0L2,21.725" />
                                                    <path d="M4.383 14.725L13.59 14.725" />
                                                    <path d="M0.5 21.725L3.52 21.725" />
                                                    <path d="M14.479 21.725L17.5 21.725" />
                                                    <path d="M22.5,21.725,18.377,9.647a.5.5,0,0,0-.946,0l-1.888,5.543" />
                                                    <path d="M16.92 16.725L20.794 16.725" />
                                                    <path d="M21.516 21.725L23.5 21.725" />
                                                </Icon>
                                            </button>
                                        }
                                        content={() => 'Match case'}
                                        offset={{ left: 0, top: 8 }}
                                    />
                                    <Tooltip
                                        position={Position.BottomCenter}
                                        target={
                                            <button
                                                className={classes.searchButtons}
                                                style={{
                                                    borderBottom: `2px solid ${
                                                        renderSearchProps.wholeWords ? 'blue' : 'transparent'
                                                    }`,
                                                }}
                                                onClick={() =>
                                                    renderSearchProps.changeWholeWords(!renderSearchProps.wholeWords)
                                                }
                                            >
                                                <Icon>
                                                    <path d="M0.500 7.498 L23.500 7.498 L23.500 16.498 L0.500 16.498 Z" />
                                                    <path d="M3.5 9.498L3.5 14.498" />
                                                </Icon>
                                            </button>
                                        }
                                        content={() => 'Match whole word'}
                                        offset={{ left: 0, top: 8 }}
                                    />
                            </div>
                            {renderSearchProps.keyword &&
                                renderSearchProps.numberOfMatches > 0 && !isMobile && (
                                    renderPDFSearchArrowButtons(renderSearchProps)
                            )}
                            </div>

                            {renderSearchProps.keyword &&
                                renderSearchProps.numberOfMatches > 0 && isMobile && (
                                    renderPDFSearchArrowButtons(renderSearchProps)
                            )}
                        </>
                    );
                }}
            </Search>
        )
    }

    const renderPDFSearchArrowButtons = (renderSearchProps) => {
        return (
            <div style={{ display: 'flex', alignItems: 'center'}}>     
                <div>
                    <Button variant="text" onClick={renderSearchProps.jumpToPreviousMatch}>
                        <FontAwesomeIcon
                            icon={faChevronUp}
                            size="1x"
                        />
                    </Button>
                </div>
                <div style={{ padding: '0 8px' }}>
                    {renderSearchProps.currentMatch} / {renderSearchProps.numberOfMatches}
                </div>
                <div >
                    <Button variant="text" onClick={renderSearchProps.jumpToNextMatch}>
                        <FontAwesomeIcon
                            icon={faChevronDown}
                            size="1x"
                        />
                    </Button>
                </div>
                <div>           
                    {renderSearchProps.keyword &&
                        renderSearchProps.numberOfMatches === 0 && (
                            <div style={{ padding: '0 8px' }}>Not found</div>
                    )}
                </div>
            </div>
        )
    }

    useEffect(() => {
        // debugger;
        if (
          id &&
          (pdfType.toLowerCase() === "protocols" ||
            pdfType.toLowerCase() === "documents")
        ) {
          //sample api GET request
        //   debugger;
          setPdfData(null);
          setLoading(true);
          ConnectService.getPDFBase64String(pdfType, id)
            .then((data) => {
            //   setLoading(false);
              setPdfData(data.base64String);
            })
            .catch((err) => {
              alert(JSON.stringify(err));
              setLoading(false);
            });
        }
      }, [id]);

      const base64toBlob = (base64) => {
        const base64WithoutPrefix = base64.substr('data:application/pdf;base64,'.length);
    
        const bytes = window.atob(base64WithoutPrefix);
        let length = bytes.length;
        let out = new Uint8Array(length);
    
        while (length--) {
            out[length] = bytes.charCodeAt(length);
        }
    
        return new Blob([out], { type: 'application/pdf' });
    };

    const getUrlFromBase64 = useCallback(() => {
        if (pdfData) {
            const url = URL.createObjectURL(base64toBlob(pdfData));
            setLoading(false);
            setPdfUrl(url);
        }
    }, [pdfData]);

    useEffect(() => {
        getUrlFromBase64();
    }, [pdfData, getUrlFromBase64]);

    return (
        <Worker workerUrl="https://unpkg.com/pdfjs-dist@2.5.207/build/pdf.worker.min.js">
            <Typography variant="subtitle2">{title}</Typography>
            {loading ? (
                <div className={classes.centerLoading}>
                    <CircularProgress color="primary" size={50} />
                </div>
            ) :
            <div
                className="rpv-core__viewer"
                style={{
                    border: '1px solid rgba(0, 0, 0, 0.3)',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '800px',
                }}
            >
                <div
                    style={{
                        alignItems: 'center',
                        backgroundColor: '#eeeeee',
                        borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
                        display: 'flex',
                        padding: '4px',
                    }}
                >
                    <Toolbar>
                        {(props) => {
                            const {
                                CurrentPageInput,
                                Download,
                                EnterFullScreen,
                                GoToNextPage,
                                GoToPreviousPage,
                                NumberOfPages,
                                Print,
                                Zoom,
                                ZoomIn,
                                ZoomOut,
                            } = props;
                            return (
                                <>
                                    <div style={{ padding: '0px 2px' }}>
                                        <ZoomOut />
                                    </div>
                                    <div style={{ padding: '0px 2px' }}>
                                        <Zoom />
                                    </div>
                                    <div style={{ padding: '0px 2px' }}>
                                        <ZoomIn />
                                    </div>
                                    <div style={{ padding: '0px 2px', marginLeft: 'auto' }}>
                                        <GoToPreviousPage />
                                    </div>
                                    <div style={{ padding: '0px 2px', width: '4rem' }}>
                                        <CurrentPageInput />
                                    </div>
                                    <div style={{ padding: '0px 2px' }}>
                                        / <NumberOfPages />
                                    </div>
                                    <div style={{ padding: '0px 2px' }}>
                                        <GoToNextPage />
                                    </div>
                                    {!isMobile && (
                                        <div style={{ padding: '0px 2px', marginLeft: 'auto' }}>
                                            <EnterFullScreen />
                                        </div>
                                    )}
                                    {isDownloadable && (
                                        <div style={{ padding: '0px 2px' }}>
                                            <Download />
                                        </div>
                                    )}
                                    <div style={{ padding: '0px 2px' }}>
                                        <Print />
                                    </div>
                                </>
                            );
                        }}
                    </Toolbar>
                </div>
                <div
                    style={{
                        alignItems: 'center',
                        backgroundColor: '#eeeeee',
                        borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
                        display: 'flex',
                        padding: '4px',
                    }}
                >
                    <Toolbar>
                        {(props) => {
                            const { Search } = props;
                            return (
                                <div style={{ padding: '0px 2px' }}>
                                    {renderPDFSearch(Search)}
                                </div>
                            );
                        }}
                    </Toolbar>
                </div>
                <div
                    style={{
                        flex: 1,
                        overflow: 'hidden',
                    }}
                >
                    {pdfUrl && (
                        <Viewer fileUrl={pdfUrl} plugins={[toolbarPluginInstance]} />
                    )}
                </div>
            </div>}
        </Worker>
    )
}

    export default PDFViewerAndToolBar;
    